/**
 * Mobile App Design - 모바일 앱 디자인 시안
 * /mobile 라우트로 접근
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HiLocationMarker,
  HiBell,
  HiX,
  HiCheckCircle,
  HiInformationCircle
} from 'react-icons/hi';
import PermissionModal from '../components/mobile/PermissionModal';
import MobileMap from '../components/mobile/MobileMap';
import SpotDetailModal from '../components/mobile/SpotDetailModal';
import PushNotificationBanner from '../components/mobile/PushNotificationBanner';
import { mockSpotsData } from '../data/mockData';

export default function MobileApp() {
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [showSpotDetail, setShowSpotDetail] = useState(false);
  const [pushNotification, setPushNotification] = useState(null);

  // 모의 푸시 알림 (스팟 진입 시뮬레이션)
  useEffect(() => {
    if (permissionsGranted) {
      // 3초 후 첫 번째 스팟 진입 알림 표시
      const timer1 = setTimeout(() => {
        setPushNotification({
          id: 1,
          spotName: mockSpotsData[0].spot_name,
          message: `${mockSpotsData[0].spot_name} 스팟 존에 진입했습니다!`,
          timestamp: new Date(),
        });
      }, 3000);

      // 8초 후 두 번째 스팟 진입 알림 표시
      const timer2 = setTimeout(() => {
        setPushNotification({
          id: 2,
          spotName: mockSpotsData[1].spot_name,
          message: `${mockSpotsData[1].spot_name} 스팟 존에 진입했습니다!`,
          timestamp: new Date(),
        });
      }, 8000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [permissionsGranted]);

  const handlePermissionGrant = () => {
    setPermissionsGranted(true);
  };

  const handleSpotClick = (spot) => {
    setSelectedSpot(spot);
    setShowSpotDetail(true);
  };

  const handleCloseSpotDetail = () => {
    setShowSpotDetail(false);
    setSelectedSpot(null);
  };

  const handleCloseNotification = () => {
    setPushNotification(null);
  };

  return (
    <div 
      className="w-full h-screen overflow-hidden relative"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      {/* 푸시 알림 배너 */}
      <AnimatePresence>
        {pushNotification && (
          <PushNotificationBanner
            notification={pushNotification}
            onClose={handleCloseNotification}
          />
        )}
      </AnimatePresence>

      {/* 권한 동의 모달 */}
      <AnimatePresence>
        {!permissionsGranted && (
          <PermissionModal onGrant={handlePermissionGrant} />
        )}
      </AnimatePresence>

      {/* 메인 콘텐츠 */}
      {permissionsGranted && (
        <motion.div
          className="w-full h-full flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* 헤더 */}
          <motion.header
            className="px-4 py-3 shadow-lg border-b-2 relative z-10"
            style={{
              background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
              borderColor: 'var(--color-primary)',
            }}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HiLocationMarker className="w-6 h-6 text-white" />
                <h1 className="text-lg font-display font-bold text-white">
                  메타보맵
                </h1>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs text-white/90">위치 서비스 활성</span>
              </div>
            </div>
          </motion.header>

          {/* 지도 영역 */}
          <div className="flex-1 relative">
            <MobileMap
              spots={mockSpotsData}
              onSpotClick={handleSpotClick}
            />
          </div>
        </motion.div>
      )}

      {/* 스팟 상세 정보 모달 */}
      <AnimatePresence>
        {showSpotDetail && selectedSpot && (
          <SpotDetailModal
            spot={selectedSpot}
            onClose={handleCloseSpotDetail}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
